import { useReducer, useCallback } from 'react';
import type {
  ImageEditorState,
  ImageEditorAction,
  EditorTool,
  BrushSettings,
  TextSettings,
  ShapeSettings,
  FilterSettings,
  CropState,
} from '../types';

const initialState: ImageEditorState = {
  activeTool: 'select',
  brushSettings: { color: '#000000', width: 4, opacity: 1 },
  textSettings: {
    fontFamily: 'Arial',
    fontSize: 24,
    fontWeight: 'normal',
    fontStyle: 'normal',
    underline: false,
    textAlign: 'left',
    fill: '#000000',
  },
  shapeSettings: {
    type: 'rect',
    fill: 'transparent',
    stroke: '#000000',
    strokeWidth: 2,
    cornerRadius: 0,
  },
  filterSettings: { brightness: 0, contrast: 0, saturation: 0 },
  crop: { isActive: false, aspectRatio: null, rect: null },
  zoom: 1,
  canvasWidth: 0,
  canvasHeight: 0,
  selectedObjectIds: [],
  fileName: 'Untitled',
  fileId: null,
  isDirty: false,
  propertiesPanelOpen: true,
  cursorPosition: { x: 0, y: 0 },
  colorUnderCursor: '#000000',
};

function reducer(state: ImageEditorState, action: ImageEditorAction): ImageEditorState {
  switch (action.type) {
    case 'SET_ACTIVE_TOOL':
      return {
        ...state,
        activeTool: action.tool,
        // Deactivate crop when switching away
        crop: action.tool !== 'crop' ? { ...state.crop, isActive: false } : state.crop,
      };
    case 'UPDATE_BRUSH_SETTINGS':
      return { ...state, brushSettings: { ...state.brushSettings, ...action.settings } };
    case 'UPDATE_TEXT_SETTINGS':
      return { ...state, textSettings: { ...state.textSettings, ...action.settings } };
    case 'UPDATE_SHAPE_SETTINGS':
      return { ...state, shapeSettings: { ...state.shapeSettings, ...action.settings } };
    case 'UPDATE_FILTER_SETTINGS':
      return { ...state, filterSettings: { ...state.filterSettings, ...action.settings } };
    case 'SET_ZOOM':
      return { ...state, zoom: Math.max(0.1, Math.min(10, action.zoom)) };
    case 'SET_CANVAS_DIMENSIONS':
      return { ...state, canvasWidth: action.width, canvasHeight: action.height };
    case 'SET_CROP':
      return { ...state, crop: { ...state.crop, ...action.crop } };
    case 'SET_SELECTION':
      return { ...state, selectedObjectIds: action.objectIds };
    case 'SET_FILE_INFO':
      return { ...state, fileName: action.fileName, fileId: action.fileId };
    case 'MARK_DIRTY':
      return { ...state, isDirty: true };
    case 'MARK_CLEAN':
      return { ...state, isDirty: false };
    case 'TOGGLE_PROPERTIES_PANEL':
      return { ...state, propertiesPanelOpen: !state.propertiesPanelOpen };
    case 'SET_CURSOR_INFO':
      return {
        ...state,
        cursorPosition: { x: action.x, y: action.y },
        colorUnderCursor: action.color,
      };
    default:
      return state;
  }
}

export function useImageEditorState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setActiveTool = useCallback(
    (tool: EditorTool) => dispatch({ type: 'SET_ACTIVE_TOOL', tool }),
    [],
  );
  const updateBrushSettings = useCallback(
    (settings: Partial<BrushSettings>) => dispatch({ type: 'UPDATE_BRUSH_SETTINGS', settings }),
    [],
  );
  const updateTextSettings = useCallback(
    (settings: Partial<TextSettings>) => dispatch({ type: 'UPDATE_TEXT_SETTINGS', settings }),
    [],
  );
  const updateShapeSettings = useCallback(
    (settings: Partial<ShapeSettings>) => dispatch({ type: 'UPDATE_SHAPE_SETTINGS', settings }),
    [],
  );
  const updateFilterSettings = useCallback(
    (settings: Partial<FilterSettings>) => dispatch({ type: 'UPDATE_FILTER_SETTINGS', settings }),
    [],
  );
  const setZoom = useCallback((zoom: number) => dispatch({ type: 'SET_ZOOM', zoom }), []);
  const setCanvasDimensions = useCallback(
    (width: number, height: number) => dispatch({ type: 'SET_CANVAS_DIMENSIONS', width, height }),
    [],
  );
  const setCrop = useCallback(
    (crop: Partial<CropState>) => dispatch({ type: 'SET_CROP', crop }),
    [],
  );
  const setSelection = useCallback(
    (objectIds: string[]) => dispatch({ type: 'SET_SELECTION', objectIds }),
    [],
  );
  const setFileInfo = useCallback(
    (fileName: string, fileId: string | null) =>
      dispatch({ type: 'SET_FILE_INFO', fileName, fileId }),
    [],
  );
  const markDirty = useCallback(() => dispatch({ type: 'MARK_DIRTY' }), []);
  const markClean = useCallback(() => dispatch({ type: 'MARK_CLEAN' }), []);
  const togglePropertiesPanel = useCallback(
    () => dispatch({ type: 'TOGGLE_PROPERTIES_PANEL' }),
    [],
  );
  const setCursorInfo = useCallback(
    (x: number, y: number, color: string) => dispatch({ type: 'SET_CURSOR_INFO', x, y, color }),
    [],
  );

  return {
    state,
    dispatch,
    setActiveTool,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateFilterSettings,
    setZoom,
    setCanvasDimensions,
    setCrop,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    setCursorInfo,
  };
}

export { initialState, reducer };
